import { locatedAnchor } from '../../../shared/routeAnchor';
import { onSyncStatus } from '../data/sync';
import { currentPosition } from '../data/geocode';
import { worthAStop } from '../logic/today';
import type { LatLng } from '../logic/route';
import { cachedSignalsAt, emptyMirror } from './shared/noLeadsYet';
import { dueGroups } from './brief/dueGroups';
import { worthStops } from './brief/worthStops';
import { dueMap } from './brief/dueMap';
import { shell } from '../app';
import { loadRoute } from '../data/dayRoutes';
import { readStoredSignals } from '../data/signals';
import { getSnapshot, listPlaybook, onStoreChange } from '../data/store';
import { ROUTE_CHANGED } from '../data/dayRoutes';
import { buildBrief, briefCount } from '../logic/brief';
import type { Brief, BriefItem } from '../logic/brief';
import { dayHeading, todayLocal } from '../logic/dates';
import { empty, linkBtn, section } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import type { View } from '../ui/router';
import { briefRow } from './brief/briefRow';

/**
 * The daily brief (D89) — the screen Artur opens instead of assembling a morning. Everything is
 * read from the local mirror, so it works standing on a sidewalk with no signal; the drafts come
 * from the mirrored message library (D88) and are copy for him to send himself.
 */

const gapsBanner = (gaps: readonly string[]): HTMLElement | null =>
  gaps.length === 0
    ? null
    : h(
        'div',
        { class: 'brief__gaps' },
        h('h2', { class: 'brief__gapstitle mono' }, 'What this brief cannot see'),
        h('ul', { class: 'brief__gapslist' }, gaps.map((g) => h('li', {}, g))),
      );

/** A section counts what is still to do; what is worked stays on the list, struck through. */
const left = (items: readonly BriefItem[]): number => items.filter((i) => !i.done).length;

const routeSubtitle = (brief: Brief): string | undefined => {
  if (!brief.route) return undefined;
  const where = brief.route.area ? ` · ${brief.route.area}` : '';
  return `${brief.route.stops} stop${brief.route.stops === 1 ? '' : 's'}${where}`;
};

export const briefView: View = ({ root }) => {
  shell.setChrome(true);
  const host = h('div', { class: 'page page--brief' });
  root.appendChild(host);
  let disposed = false;
  let sequence = 0;
  const map = dueMap();
  const list = h('div', { class: 'brief__list' });
  host.append(list, map.el);
  let anchor: LatLng | null = locatedAnchor(loadRoute());
  if (!anchor) void currentPosition(2000).then(at => {
    if (!at || disposed) return;
    anchor = at;
    void render();
  });

  const render = async () => {
    if (disposed) return;
    const mine = ++sequence;
    // per render: a phone left open past midnight must brief today, not yesterday
    const today = todayLocal();
    const [snap, stored, playbook] = await Promise.all([
      getSnapshot(),
      readStoredSignals(),
      listPlaybook(),
    ]);
    if (disposed || mine !== sequence) return;
    const brief = buildBrief({
      today,
      leads: snap.leads,
      visits: snap.visits,
      signals: stored?.list.signals ?? [],
      route: loadRoute(today),
      playbook,
    });
    const total = briefCount(brief);
    // "Brief", not "Today's brief": the phone's top bar also carries the action and the sync pill,
    // and the long title was the one that lost (QA at 390 px)
    shell.setTitle('Brief', {
      subtitle: dayHeading(today),
      action: linkBtn('Plan tomorrow', '#/route/plan', { variant: 'subtle', size: 'sm' }),
    });

    const dueIds = new Set([...brief.followUps, ...brief.stops]
      .filter(x => x.daysOverdue !== undefined).map(x => x.lead_id));
    map.show(snap.leads.filter(l => dueIds.has(l.id)));
    replaceChildren(
      list,
      total === 0 && brief.gaps.length === 0
        ? empty('Nothing due today.', 'Plan a route or open New businesses to pick the next doors.')
        : null,
      snap.leads.length ? dueGroups(brief) : emptyMirror({ signalsAt: cachedSignalsAt(stored) }),
      worthStops(worthAStop(stored, anchor)),
      section(
        {
          title: 'Calls to make',
          count: left(brief.calls),
          tone: 'accent',
          action: linkBtn('New businesses', '#/signals', { variant: 'ghost', size: 'sm' }),
        },
        brief.calls.length
          ? brief.calls.map((item) => briefRow(item))
          : empty('No new business worth a call has a phone on this device yet.'),
      ),
      section(
        {
          title: 'Walk-in route',
          count: left(brief.stops),
          action: linkBtn(brief.route ? 'Open route' : 'Plan one', '#/route', {
            variant: 'ghost',
            size: 'sm',
          }),
        },
        brief.route ? h('p', { class: 'brief__routeline mono' }, routeSubtitle(brief) ?? '') : null,
        brief.stops.length
          ? brief.stops.map((item, i) => briefRow(item, i))
          : empty('No route on this device for today.'),
      ),
      gapsBanner(brief.gaps),
    );
  };

  void render();
  const offSync = onSyncStatus(() => void render());
  const off = onStoreChange(() => void render());
  const onRoute = () => void render();
  window.addEventListener(ROUTE_CHANGED, onRoute);
  return () => {
    disposed = true;
    off();
    offSync();
    map.dispose();
    window.removeEventListener(ROUTE_CHANGED, onRoute);
  };
};
